import store from '@/store'
import { find, findIndex, cloneDeep } from 'lodash'
import utils from '@/helpers/utils'

import {
  insertGridLookUpExtraData,
  insertGridMultiLookUpExtraData,
  insertCellLookUpExtraData,
  insertCellMultiLookUpExtraData,
} from './fields/lookups'
import { insertGridUserExtraData, insertCellUserExtraData } from './fields/user'
import { insertGridDateExtraData, insertCellDateExtraData } from './fields/date'
import {
  insertGridFilesExtraData,
  insertCellFilesExtraData,
} from './fields/files'

export default {
  dataGridParse,
  insertGridLookUpExtraData,
  insertGridMultiLookUpExtraData,
  insertCellLookUpExtraData,
  insertCellMultiLookUpExtraData,
  insertGridUserExtraData,
  insertCellUserExtraData,
  insertGridDateExtraData,
  insertCellDateExtraData,
  insertGridFilesExtraData,
  insertCellFilesExtraData,
  markInheritedColumns(widgetsData) {
    if (!widgetsData) return
    if (!widgetsData.dataGrid) return

    for (let gridId in widgetsData.dataGrid) {
      const dataGrid = widgetsData.dataGrid[gridId]

      if (dataGrid.meta) {
        for (let cellKey in dataGrid.meta) {
          dataGrid.meta[cellKey].inherited = true
        }
      }
    }
  },
  extractDataForInheritedLockedDataGrids(template, inheritedData) {
    if (!inheritedData[template].widgetsData) return
    if (!inheritedData[template].widgetsData.dataGrid) return

    for (let gridId in inheritedData[template].widgetsData.dataGrid) {
      const gridData = inheritedData[template].widgetsData.dataGrid[gridId]

      if (!gridData.lockedForInherited) continue
      if (!inheritedData[template].models) continue
      if (!inheritedData[template].models[gridId]) continue

      const inheritedGridData = inheritedData[template].models[gridId]

      store.commit(
        'models_setDashboardModel',
        Object.assign(store.state.models_dashboardModel, {
          [gridId]: inheritedGridData,
        })
      )
    }
  },
  changeFormDesignerFormat(wd, format) {
    if (!wd) return
    if (!wd.dataGrid) return

    for (let gridId in wd.dataGrid) {
      const form = wd.dataGrid[gridId].rowEditorFieldsForm

      if (!form) continue

      try {
        switch (format) {
          case 'json':
            if (typeof form === 'object')
              wd.dataGrid[gridId].rowEditorFieldsForm = JSON.stringify(form)
            break
          case 'object':
            if (typeof form === 'string')
              wd.dataGrid[gridId].rowEditorFieldsForm = JSON.parse(form)
        }
      } catch (err) {
        console.log(form, format)
        console.error(err)
      }
    }
  },
  populateFormDesigner(widgetsData, inheritedData) {
    if (!widgetsData) return
    if (!widgetsData.dataGrid) return

    for (let gridId in widgetsData.dataGrid) {
      const form1 = widgetsData.dataGrid[gridId].rowEditorFieldsForm

      const populatedControls = {}

      if (!form1) continue

      for (let section of form1.sections) {
        if (!section.rows) continue

        for (let row of section.rows) {
          if (!row.controls) continue

          for (let control of row.controls) {
            populatedControls[control.name] = true
          }
        }
      }

      if (!inheritedData.dataGrid) continue
      if (!inheritedData.dataGrid[gridId]) continue

      const form2 = inheritedData.dataGrid[gridId].rowEditorFieldsForm

      for (let sectionIdx in form2.sections) {
        if (sectionIdx === 'rmIndex') continue

        if (!form1.sections) form1.sections = []

        const section = form2.sections[sectionIdx]
        let form1SectionIdx

        if (!find(form1.sections, { name: section.name })) {
          form1SectionIdx = form1.sections.length
          const newSection = cloneDeep(section)
          delete newSection.rows
          form1.sections.push(newSection)
        } else {
          form1SectionIdx = findIndex(form1.sections, { name: section.name })
        }

        for (let rowIdx in form2.sections[sectionIdx].rows) {
          if (rowIdx === 'rmIndex') continue

          if (!form1.sections[form1SectionIdx].rows)
            form1.sections[form1SectionIdx].rows = []

          const row = form2.sections[sectionIdx].rows[rowIdx]
          let form1RowIdx

          if (!find(form1.sections[form1SectionIdx].rows, { name: row.name })) {
            form1RowIdx = form1.sections[form1SectionIdx].rows.length
            const newRow = cloneDeep(row)
            delete newRow.controls
            form1.sections[form1SectionIdx].rows.push(newRow)
          } else {
            form1RowIdx = findIndex(form1.sections[form1SectionIdx].rows, {
              name: row.name,
            })
          }

          for (let controlIdx in form2.sections[sectionIdx].rows[rowIdx]
            .controls) {
            if (rowIdx === 'rmIndex') continue

            if (!form1.sections[form1SectionIdx].rows[form1RowIdx].controls)
              form1.sections[form1SectionIdx].rows[form1RowIdx].controls = []

            const control =
              form2.sections[sectionIdx].rows[rowIdx].controls[controlIdx]
            if (typeof control === 'function') continue

            if (populatedControls[control.name]) continue

            if (
              !find(
                form1.sections[form1SectionIdx].rows[form1RowIdx].controls,
                { name: control.name }
              )
            ) {
              form1.sections[form1SectionIdx].rows[form1RowIdx].controls.push(
                control
              )
            }
          }
        }
      }
    }
  },
  populateDataGridMeta(widgetsData, inheritedData) {
    if (!widgetsData) return
    if (!widgetsData.dataGrid) return
    if (!inheritedData) return
    if (!inheritedData.dataGrid) return

    for (let gridId in widgetsData.dataGrid) {
      if (!widgetsData.dataGrid[gridId]) continue
      if (!inheritedData.dataGrid[gridId]) continue

      const meta1 = widgetsData.dataGrid[gridId].meta
      const meta2 = inheritedData.dataGrid[gridId].meta

      if (!meta1 || !meta2) continue

      for (let cellKey in meta2) {
        if (!meta1[cellKey]) {
          meta1[cellKey] = cloneDeep(meta2[cellKey])
          meta1[cellKey].order = utils.count(meta1)
        }
      }
    }
  },
}

/**
 * Parse complex data in DataGrids
 * @param {Object} state - Vuex state instance
 */
function dataGridParse(state) {
  const allOptions = state.dataGrid_options
  const complexTypes = ['lookup', 'multi_lookup', 'files', 'date', 'user']

  if (!allOptions) return

  for (let gridId in allOptions) {
    const dgOptions = allOptions[gridId]

    if (!dgOptions) {
      continue
    }

    for (let cellKey in dgOptions.meta) {
      const type = dgOptions.meta[cellKey].type
      const data = { state, gridId, allOptions, dgOptions, cellKey }

      if (!complexTypes.includes(type)) continue

      switch (type) {
        case 'lookup':
          try {
            insertGridLookUpExtraData(data)
          } catch (err) {
            console.error(
              `[DataGrid parser:insertGridLookUpExtraData] grid: "${gridId}", cellKey: "${cellKey}", message: "${err.message}"`
            )
          }
          break
        case 'multi_lookup':
          try {
            insertGridMultiLookUpExtraData(data)
          } catch (err) {
            console.error(
              `[DataGrid parser:insertGridMultiLookUpExtraData] grid: "${gridId}", cellKey: "${cellKey}", message: "${err.message}"`
            )
          }
          break
        case 'user':
          try {
            insertGridUserExtraData(data)
          } catch (err) {
            console.error(
              `[DataGrid parser:insertGridUserExtraData] grid: "${gridId}", cellKey: "${cellKey}", message: "${err.message}"`
            )
          }
          break
        case 'date':
          try {
            insertGridDateExtraData(data)
          } catch (err) {
            console.error(
              `[DataGrid parser:insertGridDateExtraData] grid: "${gridId}", cellKey: "${cellKey}", message: "${err.message}"`
            )
          }
          break
        case 'files':
          try {
            insertGridFilesExtraData(data)
          } catch (err) {
            console.error(
              `[DataGrid parser:insertGridFilesExtraData] grid: "${gridId}", cellKey: "${cellKey}", message: "${err.message}"`
            )
          }
          break
      }
    }

    const dataGrid = state.models_dashboardModel[gridId]

    if (!dataGrid) continue

    for (let rowKey in dataGrid) {
      const row = dataGrid[rowKey]
      if (!row.rowKey) continue 
      
      row.$count = 1

      const meta = dgOptions.meta

      if (meta) {
        for (let cellKey in meta) {
          if (typeof row[cellKey] === 'undefined') row[cellKey] = null
        }
      }
    }
  }
}
